import { useEffect, useMemo, useState } from "react";
import {
  getDeterminateSearchActivityProgressPercentage,
  getSearchActivityElapsedMs,
  type SearchExecutionActivity,
} from "@/lib/search-activity";
import { cn } from "@/lib/utils";

/** Minimal phase labels — just enough to orient the user. */
const PHASE_LABELS: Record<string, string> = {
  preparing: "Preparing",
  queued: "Starting up",
  generating: "Generating",
  scanning: "Scanning",
  completed: "Done",
  failed: "Failed",
  interrupted: "Stopped",
};

export function SearchActivityOverlay({
  activity,
  className,
  compact = false,
  showElapsedTime = true,
}: {
  activity: SearchExecutionActivity | null | undefined;
  className?: string;
  compact?: boolean;
  showElapsedTime?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  const shimmerId = useMemo(() => `overlay-shimmer-${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    if (!activity?.isActive || !showElapsedTime) {
      return;
    }

    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [activity?.isActive, showElapsedTime]);

  if (!activity?.isActive) {
    return null;
  }

  const label = PHASE_LABELS[activity.phase] ?? activity.phase;
  const progress = getDeterminateSearchActivityProgressPercentage(activity);
  const elapsed = showElapsedTime ? getSearchActivityElapsedMs(activity, now) : null;

  // After 30 seconds with no visible progress, show a gentle elapsed time
  // so users know the system is still alive during a long run.
  const showElapsed =
    showElapsedTime && elapsed !== null && elapsed > 30_000 && progress === null;
  const elapsedMin = elapsed !== null ? Math.floor(elapsed / 60_000) : 0;
  const elapsedSec = elapsed !== null ? Math.floor((elapsed % 60_000) / 1000) : 0;

  return (
    <div
      className={cn(
        "absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[2px]",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-3",
          compact && "gap-2",
        )}
      >
        {/* Pulsing dot + label */}
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-foreground/40 duration-[2000ms]" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary-foreground/70" />
          </span>
          <span
            className={cn(
              "text-[13px] font-medium tracking-wide text-foreground/80",
              compact && "text-xs",
            )}
          >
            {label}
          </span>
        </div>

        {/* Thin progress line */}
        <div className={cn("w-28 overflow-hidden rounded-full", compact && "w-20")}>
          {progress !== null ? (
            /* Determinate — thin filled bar */
            <div className="h-[3px] w-full rounded-full bg-border/60">
              <div
                className="h-full rounded-full bg-secondary-foreground/50 transition-all duration-700 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : (
            /* Indeterminate — animated shimmer line */
            <div className="h-[3px] w-full rounded-full bg-border/40">
              <svg width="100%" height="3" className="overflow-visible">
                <defs>
                  <linearGradient id={shimmerId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="var(--secondary-foreground)" stopOpacity="0" />
                    <stop offset="50%" stopColor="var(--secondary-foreground)" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="var(--secondary-foreground)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <rect
                  width="40%"
                  height="3"
                  rx="1.5"
                  fill={`url(#${shimmerId})`}
                  className="animate-shimmer-slide"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Elapsed fallback for long waits without numeric progress */}
        {showElapsed && (
          <span className={cn("text-[11px] tabular-nums text-muted-foreground/60", compact && "text-[10px]")}>
            {elapsedMin > 0 ? `${elapsedMin}m ${elapsedSec}s` : `${elapsedSec}s`}
          </span>
        )}
      </div>
    </div>
  );
}
